import { z } from 'zod';

import { idSchema } from './id';
import { simplePaginationSchema } from './pagination';

export const knownSkillChangeTypes = [
    'TITLE_UPDATED',
    'SKILL_TYPE_UPDATED',
    'ARCHIVED',
    'DELETED',
    'REPLACED',
    'REMOVED',
] as const;

export type KnownSkillChangeType = (typeof knownSkillChangeTypes)[number];

export const knownSkillChangeScopes = ['GLOBAL', 'TENANT'] as const;

export type KnownSkillChangeScope = (typeof knownSkillChangeScopes)[number];

export const knownSkillChangeEntityTypes = ['ROLE', 'LEVEL', 'LEARNING_OPPORTUNITY', 'USER', 'TASK'] as const;

export type KnownSkillChangeEntityType = (typeof knownSkillChangeEntityTypes)[number];

export const knownSkillChangeCauses = [
    'SKILL_ARCHIVED',
    'SKILL_DELETED',
    'SKILL_REPLACED',
    'BULK_REMOVAL',
    'MANUAL',
] as const;

export type KnownSkillChangeCause = (typeof knownSkillChangeCauses)[number];

export const skillChangeEventsSorts = ['-occurredAt', 'occurredAt'] as const;

export type SkillChangeEventsSort = (typeof skillChangeEventsSorts)[number];

export const DEFAULT_SKILL_CHANGE_EVENTS_SORT: SkillChangeEventsSort = '-occurredAt';

export const isSkillChangeEventsSort = (value: string | null): value is SkillChangeEventsSort =>
    skillChangeEventsSorts.some((sort) => sort === value);

export const isKnownSkillChangeType = (value: string): value is KnownSkillChangeType =>
    knownSkillChangeTypes.some((type) => type === value);

export const isKnownSkillChangeScope = (value: string): value is KnownSkillChangeScope =>
    knownSkillChangeScopes.some((scope) => scope === value);

export const isKnownSkillChangeEntityType = (value: string): value is KnownSkillChangeEntityType =>
    knownSkillChangeEntityTypes.some((entityType) => entityType === value);

export const isKnownSkillChangeCause = (value: string): value is KnownSkillChangeCause =>
    knownSkillChangeCauses.some((cause) => cause === value);

/**
 * `changeType`, `scope`, `entityType`, and `cause` stay plain strings so a value added by the backend later renders with a
 * fallback label instead of failing the whole page.
 */
export const skillChangeEventSchema = z.object({
    actorDeleted: z.boolean().nullish(),
    actorId: idSchema.nullish(),
    actorName: z.string().nullish(),
    affectedCount: z.number().nullish(),
    cause: z.string().nullish(),
    changeType: z.string(),
    entityId: idSchema.nullish(),
    entityTitle: z.string().nullish(),
    entityType: z.string().nullish(),
    entityVersion: z.number().nullish(),
    entityVersionPublished: z.boolean().nullish(),
    id: idSchema,
    occurredAt: z.string(),
    replacedBySkillId: idSchema.nullish(),
    replacedBySkillTitle: z.string().nullish(),
    scope: z.string(),
    skillId: idSchema,
    skillTitle: z.string().nullish(),
});

export type SkillChangeEvent = z.infer<typeof skillChangeEventSchema>;

export const paginatedSkillChangeEventsSchema = z.object({
    data: skillChangeEventSchema.array(),
    pagination: simplePaginationSchema,
});

export type PaginatedSkillChangeEvents = z.infer<typeof paginatedSkillChangeEventsSchema>;

export const skillChangeEventsExportResponseSchema = z.object({
    exportId: z.string(),
});

export type SkillChangeEventsExportResponse = z.infer<typeof skillChangeEventsExportResponseSchema>;

/**
 * ISO-8601 instants; `from` is inclusive, `to` exclusive.
 */
export type SkillChangeEventsTimeRange = {
    readonly from?: string;
    readonly to?: string;
};

export type SkillChangeEventsExportRequest = SkillChangeEventsTimeRange;
